/**
 * Bill Modify Report – before/after detail popup.
 * GET /histopathology/reports/bill-modify/detail/?id=<modification_log_id>
 *
 * Compact diff view for one bill modification event: a single line-items
 * table (merged by bill_line_item id) where each changed cell shows its
 * old value struck through above the new value, followed by the bill-level
 * before/after summary.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import CommonValidation from "../../Utility/CommonValidation";
import { CurrencySymbol } from "../../Utility/Constants";
import { ImagePaths } from "../../Utility/ImagePaths";
import { getCachevalue } from "../../Utility/Services/CacheProviderService";

const closeImg = ImagePaths?.LabCloseButton?.default ?? ImagePaths?.LabCloseButton;

const LINE_COMPARE_FIELDS = ["units", "rate_per_unit", "sub_total", "discount_amount", "net_amount"];

const ROW_BG = {
  added: "#e8f5e9",
  removed: "#ffebee",
  modified: "transparent",
  unchanged: "transparent",
};

const CHANGED_BG = "#fff8e1";

function mergeLineItems(beforeLines, afterLines) {
  const before = beforeLines || [];
  const after = afterLines || [];
  const beforeById = new Map(before.map((li) => [li.id, li]));
  const afterById = new Map(after.map((li) => [li.id, li]));
  const orderedIds = before.map((li) => li.id);
  after.forEach((li) => { if (!beforeById.has(li.id)) orderedIds.push(li.id); });
  return orderedIds.map((id) => {
    const b = beforeById.get(id) || null;
    const a = afterById.get(id) || null;
    let kind = "unchanged";
    if (!b) kind = "added";
    else if (!a) kind = "removed";
    else if (
      (b.service_description || "") !== (a.service_description || "") ||
      LINE_COMPARE_FIELDS.some((f) => String(b[f] ?? "0") !== String(a[f] ?? "0"))
    ) {
      kind = "modified";
    }
    return { id, before: b, after: a, kind };
  });
}

const amt = (v) => CommonValidation.AmountFormat(+(v || 0), false);
const asIs = (v) => v ?? "-";

function DiffCell({ before, after, field, format = amt, align = "right", bold }) {
  if (!before) {
    return <TableCell align={align} sx={{ fontWeight: bold ? 700 : 400 }}>{format(after?.[field])}</TableCell>;
  }
  if (!after) {
    return <TableCell align={align} sx={{ color: "#999", textDecoration: "line-through" }}>{format(before[field])}</TableCell>;
  }
  const changed = String(before[field] ?? "0") !== String(after[field] ?? "0");
  if (!changed) {
    return <TableCell align={align} sx={{ fontWeight: bold ? 700 : 400 }}>{format(before[field])}</TableCell>;
  }
  const up = field !== "discount_amount" ? +after[field] >= +before[field] : +after[field] <= +before[field];
  return (
    <TableCell align={align} sx={{ backgroundColor: CHANGED_BG, lineHeight: 1.2, py: 0.5 }}>
      <Typography component="div" variant="caption" sx={{ color: "#999", textDecoration: "line-through", lineHeight: 1.2 }}>
        {format(before[field])}
      </Typography>
      <Typography component="div" variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, color: up ? "#2e7d32" : "#d32f2f" }}>
        {format(after[field])}
      </Typography>
    </TableCell>
  );
}

const SummaryRow = ({ label, before, after, bold }) => {
  const changed = String(before ?? "0") !== String(after ?? "0");
  return (
    <TableRow>
      <TableCell sx={{ color: "#666", fontWeight: bold ? 700 : 400 }}>{label}</TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{CurrencySymbol} {amt(before)}</TableCell>
      <TableCell
        align="right"
        sx={{ fontWeight: bold || changed ? 700 : 400, color: changed ? (+after > +before ? "#2e7d32" : "#d32f2f") : "inherit" }}
      >
        {CurrencySymbol} {amt(after)}
      </TableCell>
    </TableRow>
  );
};

const FIELD_LABELS = {
  payable_amount: "Payable Amount",
  received_amount: "Received Amount",
  status: "Status",
  sub_total: "Sub Total",
  taxable_amount: "Taxable Amount",
  overall_concession_amount: "Concession Amount",
  overall_concession_percent: "Concession %",
  round_off: "Round Off",
  tds_amount: "TDS",
  cash_amount: "Cash",
  card_amount: "Card",
  upi_amount: "UPI",
  bank_amount: "Bank",
  cheque_amount: "Cheque",
  card_transaction_no: "Card Ref #",
  upi_transaction_no: "UPI Ref #",
  bank_transaction_no: "Bank Ref #",
  cheque_transaction_no: "Cheque Ref #",
  corporate_id: "Hospital Credit",
  service_description: "Service",
  units: "Units",
  rate_per_unit: "Rate/Unit",
  discount_amount: "Discount",
  discount_percentage: "Discount %",
  net_amount: "Net Amount",
  bill_net_amount: "Bill Net Amount",
  amount: "Amount",
  specimen: "Specimen",
};
// service_details_id always changes together with service_description, which is readable.
const HIDDEN_FIELDS = new Set(["service_details_id"]);
const fieldLabel = (f) =>
  FIELD_LABELS[f] || String(f).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function HistoryTimeline({ events, needConcessionFlow }) {
  if (!events?.length) {
    return <Typography color="text.secondary" sx={{ py: 3 }}>No history recorded for this bill yet.</Typography>;
  }
  const visible = (c) =>
    !HIDDEN_FIELDS.has(c.field) &&
    (needConcessionFlow || !/concession|discount/.test(c.field));
  return (
    <TableContainer sx={{ border: "1px solid #eee", borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 700, fontSize: "0.78rem" } }}>
            <TableCell sx={{ width: 150 }}>Date & Time</TableCell>
            <TableCell sx={{ width: 170 }}>Event</TableCell>
            <TableCell>Details</TableCell>
            <TableCell sx={{ width: 120 }}>By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((e, idx) => {
            const changes = (e.details || []).filter(visible);
            return (
              <TableRow key={`${e.at}-${idx}`} sx={{ verticalAlign: "top" }}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{e.at_display || "-"}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{e.event}</TableCell>
                <TableCell>
                  {e.summary && <Typography variant="body2">{e.summary}</Typography>}
                  {changes.map((c, i) => (
                    <Typography key={i} variant="caption" component="div" color="text.secondary">
                      {fieldLabel(c.field)}:{" "}
                      {c.old != null && c.old !== "" && (
                        <Box component="span" sx={{ textDecoration: "line-through" }}>{String(c.old)}</Box>
                      )}
                      {c.old != null && c.old !== "" && " → "}
                      <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {c.new != null && c.new !== "" ? String(c.new) : "-"}
                      </Box>
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>{e.by || "-"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function BillModifyDetailPopup({ open, onClose, logId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("changes");
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Same tenant feature flag the billing screen uses to show/hide concession/discount
  // fields (HistoBillingTbl.js: needConcessionFlow) — kept consistent here.
  const needConcessionFlow = getCachevalue("need_concession_flow_in_billing") !== "false";

  useEffect(() => {
    setTab("changes");
    setHistory(null);
    if (!open || !logId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_BILL_MODIFY_DETAIL}?id=${encodeURIComponent(logId)}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setDetail(res.data.data);
        } else {
          setDetail(null);
        }
      })
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [open, logId]);

  // Whole-bill history is fetched only when its tab is first opened.
  useEffect(() => {
    if (tab !== "history" || history !== null || !detail?.bill_id) return;
    setHistoryLoading(true);
    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_REPORTS_BILL_HISTORY}?bill_id=${encodeURIComponent(detail.bill_id)}`)
      .then((res) => setHistory(res?.data?.status === "success" ? res.data.data?.events || [] : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [tab, history, detail?.bill_id]);

  const mergedLines = useMemo(
    () => mergeLineItems(detail?.before?.line_items, detail?.after?.line_items),
    [detail]
  );

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 360, maxHeight: "88vh" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="h6" fontWeight={600}>Bill Modify Detail</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Box
              component="img"
              src={typeof closeImg === "object" && closeImg?.default ? closeImg.default : closeImg}
              alt="Close"
              sx={{ height: 20, width: 20 }}
            />
          </IconButton>
        </Box>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ px: 2, minHeight: 40, borderBottom: "1px solid #e0e0e0", "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 } }}
        >
          <Tab value="changes" label="Changes" />
          <Tab value="history" label="Bill History" disabled={!detail?.bill_id} />
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 2 }}>
          {loading ? (
            <Typography color="text.secondary" sx={{ py: 3 }}>Loading...</Typography>
          ) : !detail ? (
            <Typography color="text.secondary" sx={{ py: 3 }}>No detail found.</Typography>
          ) : tab === "history" ? (
            historyLoading || history === null ? (
              <Typography color="text.secondary" sx={{ py: 3 }}>Loading...</Typography>
            ) : (
              <HistoryTimeline events={history} needConcessionFlow={needConcessionFlow} />
            )
          ) : (
            <>
              {/* Header facts */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 2, rowGap: 0.5 }}>
                <Typography variant="body2"><b>Bill Number:</b> {detail.bill_number || "-"}</Typography>
                <Typography variant="body2"><b>Patient:</b> {(detail.patient_name || "-").toUpperCase()}</Typography>
                <Typography variant="body2"><b>Modified At:</b> {detail.modified_at || "-"}</Typography>
                <Typography variant="body2"><b>Modified By:</b> {detail.modified_by || "-"}</Typography>
              </Box>
              {detail.reason && (
                <Typography variant="body2" sx={{ mb: 2 }}><b>Reason:</b> {detail.reason}</Typography>
              )}

              {/* Line items — one compact row per service, changed cells show old (struck) above new */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Service Line Items (Before → After)</Typography>
              <TableContainer sx={{ mb: 2, border: "1px solid #eee", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 700, fontSize: "0.78rem" } }}>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Units</TableCell>
                      <TableCell align="right">Rate/Unit</TableCell>
                      <TableCell align="right">Sub Total</TableCell>
                      {needConcessionFlow && <TableCell align="right">Discount</TableCell>}
                      <TableCell align="right">Net Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mergedLines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={needConcessionFlow ? 6 : 5} align="center" sx={{ py: 3 }}>No service line items</TableCell>
                      </TableRow>
                    ) : (
                      mergedLines.map((row) => (
                        <TableRow key={row.id} sx={{ backgroundColor: ROW_BG[row.kind] }}>
                          {row.kind === "removed" ? (
                            <TableCell sx={{ color: "#999", textDecoration: "line-through" }}>
                              {row.before.service_description || "-"}
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: "#d32f2f", fontWeight: 700 }}>REMOVED</Typography>
                            </TableCell>
                          ) : row.kind === "added" ? (
                            <TableCell>
                              {row.after.service_description || "-"}
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: "#2e7d32", fontWeight: 700 }}>ADDED</Typography>
                            </TableCell>
                          ) : (row.before.service_description || "") !== (row.after.service_description || "") ? (
                            <TableCell sx={{ backgroundColor: CHANGED_BG, lineHeight: 1.2, py: 0.5 }}>
                              <Typography component="div" variant="caption" sx={{ color: "#999", textDecoration: "line-through", lineHeight: 1.2 }}>{row.before.service_description || "-"}</Typography>
                              <Typography component="div" variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{row.after.service_description || "-"}</Typography>
                            </TableCell>
                          ) : (
                            <TableCell>{row.before.service_description || "-"}</TableCell>
                          )}
                          <DiffCell before={row.before} after={row.after} field="units" format={asIs} align="right" />
                          <DiffCell before={row.before} after={row.after} field="rate_per_unit" />
                          <DiffCell before={row.before} after={row.after} field="sub_total" />
                          {needConcessionFlow && <DiffCell before={row.before} after={row.after} field="discount_amount" />}
                          <DiffCell before={row.before} after={row.after} field="net_amount" bold />
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Legend */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: CHANGED_BG, border: "1px solid #ddd" }} />
                  <Typography variant="caption">Changed value</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: ROW_BG.added, border: "1px solid #ddd" }} />
                  <Typography variant="caption">Added line</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: ROW_BG.removed, border: "1px solid #ddd" }} />
                  <Typography variant="caption">Removed line</Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Bill-level summary, before vs after */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Bill Summary</Typography>
              <TableContainer sx={{ border: "1px solid #eee", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", fontWeight: 700 } }}>
                      <TableCell />
                      <TableCell align="right">Before</TableCell>
                      <TableCell align="right">After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <SummaryRow label="Sub Total" before={detail.before?.sub_total} after={detail.after?.sub_total} />
                    {needConcessionFlow && (
                      <SummaryRow label="Discount Amount" before={detail.before?.discount_amount} after={detail.after?.discount_amount} />
                    )}
                    <SummaryRow label="Net Amount" before={detail.before?.net_amount} after={detail.after?.net_amount} />
                    <SummaryRow label="Payable Amount" before={detail.before?.payable_amount} after={detail.after?.payable_amount} bold />
                    <SummaryRow label="Received Amount" before={detail.before?.received_amount} after={detail.after?.received_amount} />
                    <SummaryRow label="Pending Amount" before={detail.before?.pending_amount} after={detail.after?.pending_amount} />
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Money movement for this specific modification event (not a before/after pair) */}
              {(+detail.extra_received_amount > 0 || +detail.refund_amount > 0) && (
                <Box sx={{ display: "flex", gap: 3, mt: 1.5 }}>
                  {+detail.extra_received_amount > 0 && (
                    <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                      Extra Received: {CurrencySymbol} {amt(detail.extra_received_amount)}
                      {detail.extra_receipt_number ? ` (${detail.extra_receipt_number})` : ""}
                    </Typography>
                  )}
                  {+detail.refund_amount > 0 && (
                    <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 600 }}>
                      Refunded: {CurrencySymbol} {amt(detail.refund_amount)}
                      {detail.refund_receipt_number ? ` (${detail.refund_receipt_number})` : ""}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
